/**
 * i18n Configuration for GonkaGame
 * Defines supported locales and their properties
 */

export interface LocaleConfig {
    code: string
    name: string
    nativeName: string
    rtl: boolean
    pluralRule?: 'one-other' | 'one-few-many-other' | 'arabic' | 'none'
}

export const SUPPORTED_LOCALES: LocaleConfig[] = [
    { code: 'en', name: 'English', nativeName: 'English', rtl: false, pluralRule: 'one-other' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true, pluralRule: 'arabic' },
    { code: 'zh', name: 'Chinese', nativeName: '简体中文', rtl: false, pluralRule: 'none' },
    { code: 'fr', name: 'French', nativeName: 'Français', rtl: false, pluralRule: 'one-other' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', rtl: false, pluralRule: 'one-other' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano', rtl: false, pluralRule: 'one-other' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', rtl: false, pluralRule: 'none' },
    { code: 'ko', name: 'Korean', nativeName: '한국인', rtl: false, pluralRule: 'none' },
    { code: 'pt', name: 'Portuguese', nativeName: 'Portuguesa', rtl: false, pluralRule: 'one-other' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский', rtl: false, pluralRule: 'one-few-many-other' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', rtl: false, pluralRule: 'one-other' },
    { code: 'ka', name: 'Georgian', nativeName: 'ქართული', rtl: false, pluralRule: 'one-other' },
    { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', rtl: false, pluralRule: 'one-other' },
    { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbekcha', rtl: false, pluralRule: 'one-other' },
    { code: 'pl', name: 'Polish', nativeName: 'Polski', rtl: false, pluralRule: 'one-few-many-other' },
    { code: 'ro', name: 'Romanian', nativeName: 'Română', rtl: false, pluralRule: 'one-other' },
    { code: 'kk', name: 'Kazakh', nativeName: 'Қазақ', rtl: false, pluralRule: 'one-other' },
    { code: 'iw', name: 'Hebrew', nativeName: 'עברית', rtl: true, pluralRule: 'one-other' },
    { code: 'cn', name: 'Chinese (Legacy)', nativeName: '中国人', rtl: false, pluralRule: 'none' },
    { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', rtl: false, pluralRule: 'none' },
    { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', rtl: false, pluralRule: 'none' },
    { code: 'cz', name: 'Czech', nativeName: 'Čeština', rtl: false, pluralRule: 'one-few-many-other' },
    { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', rtl: false, pluralRule: 'one-other' },
    { code: 'ky', name: 'Kyrgyz', nativeName: 'Кыргызча', rtl: false, pluralRule: 'one-other' },
    { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycan', rtl: false, pluralRule: 'one-other' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिंदी', rtl: false, pluralRule: 'one-other' },
    { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', rtl: false, pluralRule: 'one-other' },
] as const

export const DEFAULT_LOCALE = 'en'
export const FALLBACK_LOCALE = 'en'

export const SUPPORTED_LOCALE_CODES = SUPPORTED_LOCALES.map(l => l.code)

export const RTL_LOCALES = SUPPORTED_LOCALES.filter(l => l.rtl).map(l => l.code)

export function isLocaleSupported(code: string): boolean {
    return SUPPORTED_LOCALE_CODES.includes(code)
}

export function getLocaleConfig(code: string): LocaleConfig | undefined {
    return SUPPORTED_LOCALES.find(l => l.code === code)
}

export function isRtlLocale(code: string): boolean {
    return RTL_LOCALES.includes(code)
}
